import { Injectable } from '@angular/core';
import { map, Observable, BehaviorSubject, catchError, of } from 'rxjs';

import { IPagination } from '@diktup/frontend/models';
import {
  UserType,
  BlogType,
  StockType,
  EventType,
  BarcodeType,
  RequestType,
  ProductType,
  ProgramType,
  DocumentType,
  RequestInput,
  DocumentInput,
  PromotionType,
  CurrentUserGQL,
  NewsletterType,
  MailResponseDto,
  ContentTypeType,
  JobDefinitionGQL,
  BarcodeFindInput,
  CreateRequestGQL,
  CreateDocumentGQL,
  ProductStatusEnum,
  CorporateUserType,
  JobDefinitionType,
  AverageRatingType,
  ProductVarietyEnum,
  JobApplicationType,
  PaginatedBlogsType,
  CorporateRatingType,
  JobApplicationInput,
  InternalProductType,
  CountryPaginatedType,
  VisibilityStatusEnum,
  CreateEventInviteGQL,
  CatalogueCategoryCacheGQL,
  CorporateRatingInput,
  CatalogueCategoryType,
  CorporateTemplateType,
  AddUserToNewsLetterGQL,
  SuccessResponseDtoType,
  JobDefinitionStatusEnum,
  CreateJobApplicationGQL,
  CreateRecentlyViewedGQL,
  MarkTicketAsAttendedGQL,
  FindContentTypeByTypeGQL,
  CreateCorporateRatingGQL,
  UpdateCorporateRatingGQL,
  ValidateEmailForTargetGQL,
  InternalProductFilterType,
  FindCountriesPaginationGQL,
  JobDefinitionPaginatedType,
  FindBlogsByTargetAndUrlGQL,
  CorporateUpdateRatingInput,
  GetEventByTargetAndCodeGQL,
  InternalProductPaginateType,
  SearchCatalogueCategoriesGQL,
  GetEventInvitesWithFilterGQL,
  CorporateRatingDefinitionType,
  FindBlogsByTargetPaginatedGQL,
  GetStockByTargetAndBarcodeCacheGQL,
  CorporateRatingAssignmentType,
  MarkSimpleProductAsFavoriteGQL,
  SendValidationEmailForTargetGQL,
  GetProgramsByEventPaginationGQL,
  CatalogueCategoryWithChildrenType,
  GetBarcodeByProductAndAttributesCacheGQL,
  RemoveSimpleProductFromFavoritesGQL,
  CorporateRatingWithStatsPaginatedType,
  InternalProductWithFavoriteStatusType,
  GetSimpleProductWithFavoriteStatusGQL,
  GetCorporateRatingAssignmentByTargetCacheGQL,
  GetCorporateRatingsByProductWithStatsGQL,
  GetFavoriteSimpleProductByCurrentUserGQL,
  GetCatalogueCategoriesByLayerAndParentCacheGQL,
  GetCatalogueCategoriesByTargetAndUrlKeyGQL,
  GetCurrentUserCorporateRatingsByProductGQL,
  GetRecentlyViewedProductsByUserPaginatedGQL,
  GetCorporateRatingDefinitionByReviewTypeCacheGQL,
  InternalProductByClassTypeWithFavoriteStatus,
  GetCatalogueCategoriesByTargetWithChildrenCacheGQL,
  GetAverageCorporateProductRatingsByProductGQL,
  InternalProductWithFavoriteStatusPaginateType,
  GetSimpleOngoingPromotionsByTargetPaginateGQL,
  GetSimpleProductWithFavoriteStatusByUrlKeyGQL,
  GetJobDefinitionsByTargetAndStatusPaginationGQL,
  InternalProductWithRatingsWithFavoriteStatusType,
  GetSimpleProductsByCatalogueCategoryWithFavoriteStatusGQL,
  GetSimpleProductWithProductAttributesAndFavoriteStatusGQL,
  GetSimpleProductsWithRatingsWithFavoriteStatusWithFilterGQL,
  GetSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL,
  GetSimpleProductWithProductAttributesAndFavoriteStatusByUrlKeyGQL,
  InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType,
  GetSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatusGQL,
  GetSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilterGQL,
  GetInternalProductsByClassGQL,
  ProductClassEnum,
  GetSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusGQL,
  EventInviteInput,
  GetPartnershipNetworksByTargetPaginationGQL,
  GetCorporateUsersByTargetAndWholesalerGQL,
  InsertEventTicketsGQL,
  GetEventTicketsWithFilterGQL,
  EventTicketInput,
  GetEventTicketByReferenceGQL,
} from '@sifca-monorepo/ecommerce-generator';

import { AppCookieService, StorageHelper } from '@diktup/frontend/helpers';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private limit = 20;
  private pageLimit = 50;
  private ratingLimit = 4;
  private defaultPage = 0;
  private defaultLimit = 10;
  private productsPageIndex = 0;
  private items: BehaviorSubject<any[]> = new BehaviorSubject(null);
  private blog: BehaviorSubject<BlogType> = new BehaviorSubject(null);
  private event: BehaviorSubject<EventType> = new BehaviorSubject(null);
  private barcodeIndex: BehaviorSubject<number> = new BehaviorSubject(0);
  private currentUser: BehaviorSubject<UserType> = new BehaviorSubject(null);
  private isLastRating: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private job: BehaviorSubject<JobDefinitionType> = new BehaviorSubject(null);
  private programs: BehaviorSubject<ProgramType[]> = new BehaviorSubject(null);
  private loadingProduct: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private pagination: BehaviorSubject<IPagination> = new BehaviorSubject(null);
  private isLastProducts: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private blogs: BehaviorSubject<PaginatedBlogsType> = new BehaviorSubject(null);
  private loadingProductsGroup: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private template: BehaviorSubject<CorporateTemplateType> = new BehaviorSubject(null);
  private userRating: BehaviorSubject<CorporateRatingType> = new BehaviorSubject(null);
  private productsPagination: BehaviorSubject<IPagination> = new BehaviorSubject(null);
  private jobs: BehaviorSubject<JobDefinitionPaginatedType> = new BehaviorSubject(null);
  private overallAverage: BehaviorSubject<AverageRatingType> = new BehaviorSubject(null);
  private loadingProducts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private filter: BehaviorSubject<InternalProductFilterType> = new BehaviorSubject(null);
  private newArrivals: BehaviorSubject<InternalProductType[]> = new BehaviorSubject(null);
  private loadingCategories: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private productGroup: BehaviorSubject<InternalProductType[]> = new BehaviorSubject(null);
  private recentlyViewed: BehaviorSubject<InternalProductType[]> = new BehaviorSubject([]);
  private favoriteProducts: BehaviorSubject<InternalProductType[]> = new BehaviorSubject([]);
  private corporateRating: BehaviorSubject<CorporateRatingType[]> = new BehaviorSubject(null);
  private productsByClass: BehaviorSubject<InternalProductType[]> = new BehaviorSubject(null);
  private searchedProducts: BehaviorSubject<InternalProductType[]> = new BehaviorSubject(null);
  private selectedCategory: BehaviorSubject<CatalogueCategoryType> = new BehaviorSubject(null);
  private product: BehaviorSubject<InternalProductWithFavoriteStatusType> = new BehaviorSubject(null);
  private categories: BehaviorSubject<CatalogueCategoryWithChildrenType[]> = new BehaviorSubject(null);
  private relatedProducts: BehaviorSubject<InternalProductWithFavoriteStatusType[]> = new BehaviorSubject([]);
  private productsByUrlKey: BehaviorSubject<InternalProductWithFavoriteStatusType[]> = new BehaviorSubject(null);
  private productsGroup: BehaviorSubject<InternalProductByClassTypeWithFavoriteStatus> = new BehaviorSubject(null);
  private infinteProducts: BehaviorSubject<InternalProductWithRatingsWithFavoriteStatusType[]> = new BehaviorSubject(null);
  private simpleProducts: BehaviorSubject<InternalProductWithRatingsWithFavoriteStatusType[]> = new BehaviorSubject(null);
  private initialProducts: BehaviorSubject<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> = new BehaviorSubject(null);
  private filtredProducts: BehaviorSubject<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> = new BehaviorSubject(null);

  jobLimit = 6;
  label: string;
  blogLimit = 9;
  posId: string;
  filterLimit = 8;
  toPrice: string;
  isLast: boolean;
  nameSort: number;
  searchString = '';
  fromPrice: string;
  priceSort: number;
  productsLimit = 5;
  isBilling: boolean;
  ischeckout = false;
  filterBlogLimit = 5;
  isSignedIn: boolean;
  isShipping: boolean;
  searchCategory: any;
  productClassPage = 0;
  currentUserId: string;
  createdAtSort: number;
  isSearchLoading: boolean;
  selectedAttributes: string;
  selectedBrands: string[] = [];
  corporateRatingLength: number;
  productAttributes: string[] = [];
  selectedCategories: string[] = [];

  get productsByClass$(): Observable<InternalProductType[]> {
    return this.productsByClass.asObservable();
  }

  get loadingCategories$(): Observable<boolean> {
    return this.loadingCategories.asObservable();
  }

  get productsPagination$(): Observable<IPagination> {
    return this.productsPagination.asObservable();
  }

  get overallAverage$(): Observable<AverageRatingType> {
    return this.overallAverage.asObservable();
  }

  get filter$(): Observable<InternalProductFilterType> {
    return this.filter.asObservable();
  }
  get loadingProducts$(): Observable<boolean> {
    return this.loadingProducts.asObservable();
  }
  get template$(): Observable<CorporateTemplateType> {
    return this.template.asObservable();
  }
  set template$(value: any) {
    this.template.next(value);
  }
  get barcodeIndex$(): Observable<number> {
    return this.barcodeIndex.asObservable();
  }
  set barcodeIndex$(value: any) {
    this.barcodeIndex.next(value);
  }
  get jobs$(): Observable<JobDefinitionPaginatedType> {
    return this.jobs.asObservable();
  }
  get job$(): Observable<JobDefinitionType> {
    return this.job.asObservable();
  }
  get blogs$(): Observable<PaginatedBlogsType> {
    return this.blogs.asObservable();
  }
  get blog$(): Observable<BlogType> {
    return this.blog.asObservable();
  }
  get relatedProducts$(): Observable<InternalProductWithFavoriteStatusType[]> {
    return this.relatedProducts.asObservable();
  }
  set relatedProducts$(value: any) {
    this.relatedProducts.next(value);
  }
  get product$(): Observable<InternalProductWithFavoriteStatusType> {
    return this.product.asObservable();
  }
  set product$(value: any) {
    this.product.next(value);
  }
  get items$(): Observable<any[]> {
    return this.items.asObservable();
  }
  set items$(value: any) {
    this.items.next(value);
  }
  get favoriteProducts$(): Observable<InternalProductType[]> {
    return this.favoriteProducts.asObservable();
  }
  set favoriteProducts$(value: any) {
    this.favoriteProducts.next(value);
  }
  get productsGroup$(): Observable<InternalProductByClassTypeWithFavoriteStatus> {
    return this.productsGroup.asObservable();
  }
  get newArrivals$(): Observable<InternalProductType[]> {
    return this.newArrivals.asObservable();
  }

  get userRating$(): Observable<CorporateRatingType> {
    return this.userRating.asObservable();
  }
  set userRating$(value: any) {
    this.userRating.next(value);
  }

  get corporateRating$(): Observable<CorporateRatingType[]> {
    return this.corporateRating.asObservable();
  }
  set corporateRating$(value: any) {
    this.corporateRating.next(value);
  }
  get categories$(): Observable<CatalogueCategoryWithChildrenType[]> {
    return this.categories.asObservable();
  }
  get selectedCategory$(): Observable<CatalogueCategoryType> {
    return this.selectedCategory.asObservable();
  }
  get pagination$(): Observable<IPagination> {
    return this.pagination.asObservable();
  }
  set pagination$(value: any) {
    this.pagination.next(value);
  }
  get searchedProducts$(): Observable<InternalProductType[]> {
    return this.searchedProducts.asObservable();
  }
  set searchedProducts$(value: any) {
    this.searchedProducts.next(value);
  }
  set recentlyViewed$(value: any) {
    this.recentlyViewed.next(value);
  }
  get recentlyViewed$(): Observable<InternalProductType[]> {
    return this.recentlyViewed.asObservable();
  }
  get initialProducts$(): Observable<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> {
    return this.initialProducts.asObservable();
  }
  get infinteProducts$(): Observable<InternalProductWithRatingsWithFavoriteStatusType[]> {
    return this.infinteProducts.asObservable();
  }
  set infinteProducts$(value: any) {
    this.infinteProducts.next(value);
  }
  
  get simpleProducts$(): Observable<InternalProductWithRatingsWithFavoriteStatusType[]> {
    return this.simpleProducts.asObservable();
  }
  set simpleProducts$(value: any) {
    this.simpleProducts.next(value);
  }
  
  get filtredProducts$(): Observable<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> {
    return this.filtredProducts.asObservable();
  }
  set filtredProducts$(value: any) {
    this.filtredProducts.next(value);
  }
  get isLastRating$(): Observable<boolean> {
    return this.isLastRating.asObservable();
  }
  get isLastProducts$(): Observable<boolean> {
    return this.isLastProducts.asObservable();
  }
  set productsByUrlKey$(value: any) {
    this.productsByUrlKey.next(value);
  }
  get productsByUrlKey$(): Observable<InternalProductWithFavoriteStatusType[]> {
    return this.productsByUrlKey.asObservable();
  }
  set productsPageIndex$(value: any) {
    this.productsPageIndex = value;
  }
  get productsPageIndex$() {
    return this.productsPageIndex;
  }
  set limit$(value: any) {
    this.limit = value;
  }
  get limit$() {
    return this.limit;
  }
  set pageIndex$(value: any) {
    this.productsPageIndex = value;
  }
  get pageIndex$() {
    return this.productsPageIndex;
  }
  get ratingLimit$() {
    return this.ratingLimit;
  }
  set filterLimit$(value: number) {
    this.filterLimit = value;
  }
  get filterLimit$() {
    return this.filterLimit;
  }
  get productGroup$(): Observable<InternalProductType[]> {
    return this.productGroup.asObservable();
  }
  get loadingProductsGroup$(): Observable<boolean> {
    return this.loadingProductsGroup.asObservable();
  }
  get currentUser$(): Observable<UserType> {
    return this.currentUser.asObservable();
  }
  set currentUser$(value: any) {
    this.currentUser.next(value);
  }

  get event$(): Observable<EventType> {
    return this.event.asObservable();
  }

  get programs$(): Observable<ProgramType[]> {
    return this.programs.asObservable();
  }

  get loadingProduct$(): Observable<boolean> {
    return this.loadingProduct.asObservable();
  }

  constructor(
    private storageHelper: StorageHelper,
    private currentUserGQL: CurrentUserGQL,
    private cookiesService: AppCookieService,
    private createRequestGQL: CreateRequestGQL,
    private jobDefinitionGQL: JobDefinitionGQL,
    private createDocumentGQL: CreateDocumentGQL,
    private insertEventTickets: InsertEventTicketsGQL,
    private createEventInviteGQL: CreateEventInviteGQL,
    private catalogueCategoryGQL: CatalogueCategoryCacheGQL,
    private addUserToNewsLetterGQL: AddUserToNewsLetterGQL,
    private markTicketAsAttendedGQL: MarkTicketAsAttendedGQL,
    private createRecentlyViewedGQL: CreateRecentlyViewedGQL,
    private createJobApplicationGQL: CreateJobApplicationGQL,
    private findContentTypeByTypeGQL: FindContentTypeByTypeGQL,
    private createCorporateRatingGQL: CreateCorporateRatingGQL,
    private updateCorporateRatingGQL: UpdateCorporateRatingGQL,
    private validateEmailForTargetGQL: ValidateEmailForTargetGQL,
    private findCountriesPaginationGQL: FindCountriesPaginationGQL,
    private getEventByTargetAndCodeGQL: GetEventByTargetAndCodeGQL,
    private findBlogsByTargetAndUrlGQL: FindBlogsByTargetAndUrlGQL,
    private getEventTicketsWithFilter: GetEventTicketsWithFilterGQL,
    private getEventInvitesWithFilterGQL: GetEventInvitesWithFilterGQL,
    private searchCatalogueCategoriesGQL: SearchCatalogueCategoriesGQL,
    private findBlogsByTargetPaginatedGQL: FindBlogsByTargetPaginatedGQL,
    private getInternalProductsByClassGQL: GetInternalProductsByClassGQL,
    private getStockByTargetAndBarcodeGQL: GetStockByTargetAndBarcodeCacheGQL,
    private markSimpleProductAsFavoriteGQL: MarkSimpleProductAsFavoriteGQL,
    private getProgramsByEventPaginationGQL: GetProgramsByEventPaginationGQL,
    private sendValidationEmailForTargetGQL: SendValidationEmailForTargetGQL,
    private getEventTicketByReferenceGQL: GetEventTicketByReferenceGQL,
    private removeSimpleProductFromFavoritesGQL: RemoveSimpleProductFromFavoritesGQL,
    private getBarcodeByProductAndAttributesGQL: GetBarcodeByProductAndAttributesCacheGQL,
    private getSimpleProductWithFavoriteStatusGQL: GetSimpleProductWithFavoriteStatusGQL,
    private getCorporateRatingAssignmentByTargetGQL: GetCorporateRatingAssignmentByTargetCacheGQL,
    private getFavoriteSimpleProductByCurrentUserGQL: GetFavoriteSimpleProductByCurrentUserGQL,
    private getCorporateRatingsByProductWithStatsGQL: GetCorporateRatingsByProductWithStatsGQL,
    private getCorporateUsersByTargetAndWholesalerGQL: GetCorporateUsersByTargetAndWholesalerGQL,
    private getPartnershipNetworksByTargetPaginationGQL: GetPartnershipNetworksByTargetPaginationGQL,
    private getCatalogueCategoriesByLayerAndParentGQL: GetCatalogueCategoriesByLayerAndParentCacheGQL,
    private getCatalogueCategoriesByTargetAndUrlKeyGQL: GetCatalogueCategoriesByTargetAndUrlKeyGQL,
    private getCurrentUserCorporateRatingsByProductGQL: GetCurrentUserCorporateRatingsByProductGQL,
    private getRecentlyViewedProductsByUserPaginatedGQL: GetRecentlyViewedProductsByUserPaginatedGQL,
    private getCorporateRatingDefinitionByReviewTypeGQL: GetCorporateRatingDefinitionByReviewTypeCacheGQL,
    private getSimpleProductWithFavoriteStatusByUrlKeyGQL: GetSimpleProductWithFavoriteStatusByUrlKeyGQL,
    private getAverageCorporateProductRatingsByProductGQL: GetAverageCorporateProductRatingsByProductGQL,
    private getSimpleOngoingPromotionsByTargetPaginateGQL: GetSimpleOngoingPromotionsByTargetPaginateGQL,
    private getCatalogueCategoriesByTargetWithChildrenGQL: GetCatalogueCategoriesByTargetWithChildrenCacheGQL,
    private getJobDefinitionsByTargetAndStatusPaginationGQL: GetJobDefinitionsByTargetAndStatusPaginationGQL,
    private getSimpleProductsByCatalogueCategoryWithFavoriteStatusGQL: GetSimpleProductsByCatalogueCategoryWithFavoriteStatusGQL,
    private getSimpleProductWithProductAttributesAndFavoriteStatusGQL: GetSimpleProductWithProductAttributesAndFavoriteStatusGQL,
    private getSimpleProductsWithRatingsWithFavoriteStatusWithFilterGQL: GetSimpleProductsWithRatingsWithFavoriteStatusWithFilterGQL,
    private getSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL: GetSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL,
    private getSimpleProductWithProductAttributesAndFavoriteStatusByUrlKeyGQL: GetSimpleProductWithProductAttributesAndFavoriteStatusByUrlKeyGQL,
    private getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusGQL: GetSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusGQL,
    private getSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatusGQL: GetSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatusGQL,
    private getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilterGQL: GetSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilterGQL,
  ) {}

  getSimpleProductWithProductAttributesAndFavoriteStatus(id: string): Observable<InternalProductWithFavoriteStatusType> {
    this.loadingProduct.next(true);
    return this.getSimpleProductWithProductAttributesAndFavoriteStatusGQL
      .fetch({
        id,
        ...(localStorage.getItem('elvklang') && localStorage.getItem('elvklang') !== 'none'
          ? { language: localStorage.getItem('elvklang') }
          : {}),
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingProduct.next(false);
          this.product.next(data.getSimpleProductWithProductAttributesAndFavoriteStatus);
          return data.getSimpleProductWithProductAttributesAndFavoriteStatus;
        }),
      );
  }

  getInternalProductsByClass(classification: ProductClassEnum): Observable<InternalProductType[]> {
    this.loadingProductsGroup.next(true);
    return this.getInternalProductsByClassGQL
      .fetch({
        target: { pos: this.storageHelper.getData('posId') },
        classification,
        pagination: { page: this.productClassPage, limit: this.pageLimit },
      })
      .pipe(
        catchError(() => {
          this.loadingProductsGroup.next(false);
          return of(null);
        }),
        map(({ data }: any) => {
          this.loadingProductsGroup.next(false);
          if (classification === ProductClassEnum.NEW_ARRIVALS) {
            this.newArrivals.next(data?.getInternalProductsByClass.objects);
          }
          this.productsByClass.next(data?.getInternalProductsByClass.objects);
          return data?.getInternalProductsByClass;
        }),
      );
  }

  getSimpleProductsWithRatingsWithFavoriteStatusWithFilter(
    posId?: string,
  ): Observable<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> {
    this.loadingProducts.next(true);
    return this.getSimpleProductsWithRatingsWithFavoriteStatusWithFilterGQL
      .fetch({
        input: {
          pagination: { page: this.productsPageIndex, limit: this.productsLimit },
          filter: {
            target: { pos: posId ? [posId] : [this.storageHelper.getData('posId')] },
            variety: [ProductVarietyEnum.PRODUCT],
            status: [ProductStatusEnum.ACTIVE],
            ...(this.productAttributes.length ? { attributesValues: this.productAttributes } : []),
            ...(this.selectedBrands.length ? { brand: this.selectedBrands } : []),
            ...(this.selectedCategories.length ? { catalogueCategory: this.selectedCategories } : []),
            ...(this.fromPrice && this.fromPrice !== '0' ? { fromPrice: this.fromPrice } : ''),
            ...(this.toPrice ? { toPrice: this.toPrice } : ''),
          },
          ...(this.searchString ? { searchString: this.searchString } : ''),
        },
        ...(this.createdAtSort || this.priceSort || this.nameSort
          ? {
              sort: [
                ...(this.createdAtSort ? [{ createdAt: this.createdAtSort }] : []),
                ...(this.nameSort ? [{ name: this.nameSort }] : []),
                ...(this.priceSort ? [{ price: this.priceSort }] : []),
              ],
            }
          : {}),
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingProducts.next(false);
          this.infinteProducts.next([
            ...(this.infinteProducts.value || []),
            ...data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter.objects,
          ]);
          this.isLastProducts.next(data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter.isLast);
          this.filtredProducts.next(data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter);
          this.filter.next(data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter.filter);
          if (this.searchString === '') {
            this.initialProducts.next(data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter);
          }
          this.productsPagination.next({
            page: this.productsPageIndex,
            size: this.productsLimit,
            length: data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter?.count,
          });
          return data.getSimpleProductsWithRatingsWithFavoriteStatusWithFilter;
        }),
      );
  }

  getRelatedProducts(catalogueCategory: string[]): Observable<InternalProductWithRatingsWithFavoriteStatusType[]> {
    return this.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusGQL
      .fetch(
        {
          input: {
            pagination: { page: this.productsPageIndex, limit: this.productsLimit },
            filter: {
              target: { pos: [this.storageHelper.getData('posId')] },
              variety: [ProductVarietyEnum.PRODUCT],
              status: [ProductStatusEnum.ACTIVE],
              catalogueCategory,
            },
          },
        },
        { fetchPolicy: 'network-only' },
      )
      .pipe(
        map(({ data }: any) => {
          this.relatedProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus.objects);
          return data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus;
        }),
      );
  }

  getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus(
    posId?: string,
  ): Observable<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> {
    this.loadingProducts.next(true);
    return this.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusGQL
      .fetch({
        input: {
          pagination: { page: this.productsPageIndex, limit: this.productsLimit },
          filter: {
            target: { pos: posId ? [posId] : [this.storageHelper.getData('posId')] },
            variety: [ProductVarietyEnum.PRODUCT],
            status: [ProductStatusEnum.ACTIVE],
            ...(this.productAttributes.length ? { attributesValues: this.productAttributes } : []),
            ...(this.selectedBrands.length ? { brand: this.selectedBrands } : []),
            ...(this.selectedCategories.length ? { catalogueCategory: this.selectedCategories } : []),
            ...(this.fromPrice && this.fromPrice !== '0' ? { fromPrice: this.fromPrice } : ''),
            ...(this.toPrice ? { toPrice: this.toPrice } : ''),
          },
          ...(this.searchString ? { searchString: this.searchString } : ''),
        },
        ...(this.createdAtSort || this.priceSort || this.nameSort
          ? {
              sort: [
                ...(this.createdAtSort ? [{ createdAt: this.createdAtSort }] : []),
                ...(this.nameSort ? [{ name: this.nameSort }] : []),
                ...(this.priceSort ? [{ price: this.priceSort }] : []),
              ],
            }
          : {}),
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingProducts.next(false);
          this.infinteProducts.next([
            ...(this.infinteProducts.value || []),
            ...data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus.objects,
          ]);
          this.isLastProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus.isLast);
          this.filtredProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus);
          this.filter.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus.filter);
          if (this.searchString === '') {
            this.initialProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus);
          }
          this.productsPagination.next({
            page: this.productsPageIndex,
            size: this.productsLimit,
            length: data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus?.count,
          });
          return data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatus;
        }),
      );
  }

  getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter(
    posId?: string,
    reset = true
  ): Observable<InternalProductWithRatingsPaginateWithFavoriteStatusWithFilterType> {
    if (reset) {
      this.loadingProducts.next(true);
    }
    return this.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilterGQL
      .fetch({
        input: {
          pagination: { page: this.productsPageIndex, limit: this.productsLimit },
          filter: {
            target: { pos: posId ? [posId] : [this.storageHelper.getData('posId')] },
            variety: [ProductVarietyEnum.PRODUCT],
            status: [ProductStatusEnum.ACTIVE],
            ...(this.productAttributes.length ? { attributesValues: this.productAttributes } : []),
            ...(this.selectedBrands.length ? { brand: this.selectedBrands } : []),
            ...(this.selectedCategories.length ? { catalogueCategory: this.selectedCategories } : []),
            ...(this.fromPrice && this.fromPrice !== '0' ? { fromPrice: this.fromPrice } : ''),
            ...(this.toPrice ? { toPrice: this.toPrice } : ''),
          },
          ...(this.searchString ? { searchString: this.searchString } : ''),
        },
        ...(this.createdAtSort || this.priceSort || this.nameSort
          ? {
              sort: [
                ...(this.createdAtSort ? [{ createdAt: this.createdAtSort }] : []),
                ...(this.nameSort ? [{ name: this.nameSort }] : []),
                ...(this.priceSort ? [{ price: this.priceSort }] : []),
              ],
            }
          : {}),
      })
      .pipe(
        map(({ data }: any) => {
          this.loadingProducts.next(false);
          this.simpleProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter.objects);
          this.infinteProducts.next([
            ...(this.infinteProducts.value || []),
            ...data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter.objects,
          ]);
          this.isLastProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter.isLast);
          this.filtredProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter);
          this.filter.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter.filter);
          if (this.searchString === '') {
            this.initialProducts.next(data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter);
          }
          this.productsPagination.next({
            page: this.productsPageIndex,
            size: this.productsLimit,
            length: data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter?.count,
          });
          return data.getSimpleProductsWithRatingsWithProductAttributesAndFavoriteStatusWithFilter;
        }),
      );
  }

  getProgramsByEventPagination(event: string): Observable<ProgramType[]> {
    return this.getProgramsByEventPaginationGQL
      .fetch({
        pagination: { limit: 2000 },
        event,
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            this.programs.next(data.getProgramsByEventPagination.objects);
            return data.getProgramsByEventPagination.objects;
          }
        }),
      );
  }

  getEventByTargetAndCode(code?: string): Observable<EventType[]> {
    return this.getEventByTargetAndCodeGQL
      .fetch({
        code,
        target: { pos: this.storageHelper.getData('posId') },
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            this.event.next(data.getEventByTargetAndCode);
            return data.getEventByTargetAndCode;
          }
        }),
      );
  }

  searchCatalogueCategories(): Observable<CatalogueCategoryType[]> {
    return this.searchCatalogueCategoriesGQL.fetch({ searchString: this.searchCategory, pagination: { page: 0, limit: 1 } }).pipe(
      map(({ data }: any) => {
        if (data) {
          this.categories.next(data.searchCatalogueCategories);
          return data.searchCatalogueCategories;
        }
      }),
    );
  }

  getCatalogueCategoriesByLayerAndParent(posId: string): Observable<CatalogueCategoryWithChildrenType[]> {
    this.loadingCategories.next(true);
    return this.getCatalogueCategoriesByLayerAndParentGQL
      .fetch({ layer: 1, target: { pos: posId ? posId : this.storageHelper.getData('posId') } })
      .pipe(
        map(({ data }: any) => {
          this.loadingCategories.next(false);
          if (data) {
            this.categories.next(data.getCatalogueCategoriesByLayerAndParentCache);
            return data.getCatalogueCategoriesByLayerAndParentCache;
          }
        }),
      );
  }

  getCatalogueCategoriesByTargetWithChildren(): Observable<CatalogueCategoryWithChildrenType[]> {
    return this.getCatalogueCategoriesByTargetWithChildrenGQL.fetch({ target: { pos: this.storageHelper.getData('posId') } }).pipe(
      map(({ data }: any) => {
        if (data) {
          this.categories.next(data.getCatalogueCategoriesByTargetWithChildrenCache);
          return data.getCatalogueCategoriesByTargetWithChildrenCache;
        }
      }),
    );
  }

  getCategoryById(id: string): Observable<CatalogueCategoryType> {
    return this.catalogueCategoryGQL.fetch({ id }).pipe(
      map(({ data }: any) => {
        this.selectedCategory.next(data.catalogueCategoryCache as any);
        return data.catalogueCategoryCache as any;
      }),
    );
  }

  getCategoryByUrl(urlKey: string): Observable<CatalogueCategoryType> {
    return this.getCatalogueCategoriesByTargetAndUrlKeyGQL.fetch({ urlKey, target: { pos: this.storageHelper.getData('posId') } }).pipe(
      map(({ data }: any) => {
        this.selectedCategory.next(data.getCatalogueCategoriesByTargetAndUrlKey);
        return data.getCatalogueCategoriesByTargetAndUrlKey;
      }),
    );
  }

  validateEmailForTarget(token: string): Observable<CorporateUserType> {
    return this.validateEmailForTargetGQL.mutate({ token }).pipe(
      map(({ data }: any) => {
        return data.validateEmailForTarget;
      }),
    );
  }

  getSimpleOngoingPromotionsByTargetPaginate(pos: string): Observable<PromotionType[]> {
    return this.getSimpleOngoingPromotionsByTargetPaginateGQL.fetch({ target: { pos }, pagination: { page: 0, limit: 1 } }).pipe(
      map(({ data }: any) => {
        return data.getSimpleOngoingPromotionsByTargetPaginate.objects;
      }),
    );
  }

  getSimpleProductsByCatalogueCategoryWithFavoriteStatus(
    id: string,
    page: number = this.defaultPage,
    limit: number = this.defaultLimit,
  ): Observable<InternalProductWithFavoriteStatusPaginateType> {
    return this.getSimpleProductsByCatalogueCategoryWithFavoriteStatusGQL
      .fetch({ status: [ProductStatusEnum.ACTIVE], catalogueCategoryIds: [id], pagination: { page, limit } })
      .pipe(
        map(({ data }: any) => {
          this.relatedProducts.next(data.getSimpleProductsByCatalogueCategoryWithFavoriteStatus.objects);
          return data.getSimpleProductsByCatalogueCategoryWithFavoriteStatus;
        }),
      );
  }

  getSimpleProductWithFavoriteStatus(id: string): Observable<InternalProductWithFavoriteStatusType> {
    return this.getSimpleProductWithFavoriteStatusGQL.fetch({ id }).pipe(
      map(({ data }: any) => {
        this.product.next(data.getSimpleProductWithFavoriteStatus);
        return data.getSimpleProductWithFavoriteStatus;
      }),
    );
  }

  getSimpleProductWithProductAttributesAndFavoriteStatusByUrlKey(urlKey: string): Observable<InternalProductWithFavoriteStatusType> {
    return this.getSimpleProductWithProductAttributesAndFavoriteStatusByUrlKeyGQL
      .fetch({ urlKey, target: { pos: this.storageHelper.getData('posId') } })
      .pipe(
        map(({ data }: any) => {
          this.product.next(data.getSimpleProductWithProductAttributesAndFavoriteStatusByUrlKey);
          return data.getSimpleProductWithProductAttributesAndFavoriteStatusByUrlKey;
        }),
      );
  }

  getSimpleProductWithFavoriteStatusByUrlKey(pos: string, urlKey: string): Observable<InternalProductWithFavoriteStatusType> {
    return this.getSimpleProductWithFavoriteStatusByUrlKeyGQL.fetch({ urlKey, target: { pos } }).pipe(
      map(({ data }: any) => {
        this.product.next(data.getSimpleProductWithFavoriteStatusByUrlKey);
        return data.getSimpleProductWithFavoriteStatusByUrlKey;
      }),
    );
  }

  getBarcodeByProductAndAttributes(input: BarcodeFindInput): Observable<BarcodeType> {
    return this.getBarcodeByProductAndAttributesGQL.fetch({ input }).pipe(
      map(({ data }: any) => {
        if (data) {
          return data.getBarcodeByProductAndAttributesCache;
        }
      }),
    );
  }

  getCorporateRatingsByProductWithStats(productId: string): Observable<CorporateRatingWithStatsPaginatedType> {
    return this.getCorporateRatingsByProductWithStatsGQL
      .fetch({ productId, pagination: { page: this.productsPageIndex, limit: this.ratingLimit } }, { fetchPolicy: 'network-only' })
      .pipe(
        map(({ data }: any) => {
          this.corporateRatingLength = data.getCorporateRatingsByProductWithStats.count;
          this.corporateRating.next(data.getCorporateRatingsByProductWithStats.objects);
          this.isLastRating.next(data.getCorporateRatingsByProductWithStats.isLast);
          return data.getCorporateRatingsByProductWithStats;
        }),
      );
  }

  loadMoreCorporateRating(productId: string): Observable<CorporateRatingWithStatsPaginatedType> {
    return this.getCorporateRatingsByProductWithStatsGQL
      .fetch({ productId, pagination: { page: this.productsPageIndex, limit: this.ratingLimit } })
      .pipe(
        map(({ data }: any) => {
          this.corporateRatingLength = data.getCorporateRatingsByProductWithStats.count;
          this.corporateRating.next([...(this.corporateRating.value || []), ...data.getCorporateRatingsByProductWithStats.objects]);
          this.isLastRating.next(data.getCorporateRatingsByProductWithStats.isLast);
          return data.getCorporateRatingsByProductWithStats;
        }),
      );
  }

  getAverageCorporateProductRatingsByProduct(productId: string): Observable<AverageRatingType> {
    return this.getAverageCorporateProductRatingsByProductGQL.fetch({ productId }, { fetchPolicy: 'network-only' }).pipe(
      map(({ data }: any) => {
        this.overallAverage.next(data.getAverageCorporateProductRatingsByProduct);
        return data.getAverageCorporateProductRatingsByProduct;
      }),
    );
  }

  createCorporateRating(pos: string, input: CorporateRatingInput): Observable<CorporateRatingType> {
    return this.createCorporateRatingGQL.mutate({ input: { ...input, target: { pos } } }).pipe(
      map(({ data }: any) => {
        this.corporateRating.next([...this.corporateRating.value, data.createCorporateRating]);
        return data.createCorporateRating;
      }),
    );
  }

  getCurrentUserCorporateRatingsByProduct(productId: string): Observable<CorporateRatingType> {
    return this.getCurrentUserCorporateRatingsByProductGQL.fetch({ productId }, { fetchPolicy: 'network-only' }).pipe(
      map(({ data }: any) => {
        this.userRating.next(data.getCurrentUserCorporateRatingsByProduct);
        return data.getCurrentUserCorporateRatingsByProduct;
      }),
    );
  }

  updateCorporateRating(input: CorporateUpdateRatingInput, id: string): Observable<CorporateRatingType> {
    return this.updateCorporateRatingGQL.mutate({ input, id }).pipe(
      map(({ data }: any) => {
        const list = [...this.corporateRating.value!];
        const idx = list.findIndex((a) => a.id === id);
        if (idx > -1) {
          list[idx] = data.updateCorporateRating;
          this.corporateRating.next(list);
        }
        return data.updateCorporateRating;
      }),
    );
  }

  getCorporateRatingAssignmentByTarget(pos: string): Observable<CorporateRatingAssignmentType[]> {
    return this.getCorporateRatingAssignmentByTargetGQL.fetch({ target: { pos } }).pipe(
      map(({ data }: any) => {
        return data.getCorporateRatingAssignmentByTargetCache;
      }),
    );
  }

  getCorporateRatingDefinitionByReviewType(reviewType: string): Observable<CorporateRatingDefinitionType[]> {
    return this.getCorporateRatingDefinitionByReviewTypeGQL.fetch({ reviewType }).pipe(
      map(({ data }: any) => {
        return data.getCorporateRatingDefinitionByReviewTypeCache;
      }),
    );
  }

  getSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatus(
    urlKey: string,
    page: number = this.defaultPage,
    limit: number = this.pageLimit,
  ): Observable<InternalProductWithFavoriteStatusPaginateType> {
    return this.getSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatusGQL
      .fetch({ urlKey, pagination: { page, limit }, target: { pos: this.storageHelper.getData('posId') } })
      .pipe(
        map(({ data }: any) => {
          this.productsByUrlKey.next(data.getSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatus.objects);
          return data.getSimpleProductsByTargetAndCatalogueCategoryUrlKeyWithFavoriteStatus;
        }),
      );
  }

  getStockByTargetAndBarcode(barcodeId: string): Observable<StockType> {
    return this.getStockByTargetAndBarcodeGQL.fetch({ target: { pos: this.storageHelper.getData('posId') }, barcode: barcodeId }).pipe(
      map(({ data }: any) => {
        return data.getStockByTargetAndBarcodeCache;
      }),
    );
  }

  sendValidationEmailForTarget(email: string): Observable<MailResponseDto> {
    return this.sendValidationEmailForTargetGQL
      .mutate({
        target: { pos: this.storageHelper.getData('posId') },
        email,
        subject: 'Félicitations ! Le courriel de validation a été envoyé avec succès à votre address email',
      })
      .pipe(
        map(({ data }: any) => {
          return data.sendValidationEmailForTarget;
        }),
      );
  }

  getSimpleProductsByTargetGroupedByClassWithFavoriteStatus(pos: string): Observable<InternalProductByClassTypeWithFavoriteStatus> {
    this.loadingProductsGroup.next(true);
    return this.getSimpleProductsByTargetGroupedByClassWithFavoriteStatusGQL
      .fetch({
        target: { pos },
        status: [ProductStatusEnum.ACTIVE],
      })
      .pipe(
        catchError(() => {
          this.loadingProductsGroup.next(false);
          return of(null);
        }),
        map(({ data }: any) => {
          this.loadingProductsGroup.next(false);
          this.productsGroup.next(data?.getSimpleProductsByTargetGroupedByClassWithFavoriteStatus);
          return data?.getSimpleProductsByTargetGroupedByClassWithFavoriteStatus;
        }),
      );
  }

  findCountriesPagination(): Observable<CountryPaginatedType> {
    return this.findCountriesPaginationGQL.fetch().pipe(
      map(({ data }: any) => {
        return data.findCountriesPagination.objects;
      }),
    );
  }

  addToLocalCart(addedItem) {
    this.items.next([...(this.items.value || []), addedItem]);
    this.saveCart();
  }

  addQuantity(updatedItem) {
    const index = this.items.value.findIndex((o) => o.barcode.id === updatedItem.barcode.id);
    this.items.value[index].quantity += 1;
    this.items.next(this.items.value);
    this.saveCart();
  }

  reduceQuantity(updatedItem) {
    const index = this.items.value.findIndex((o) => o.barcode.id === updatedItem.barcode.id);
    this.items[index].value.quantity -= 1;
    this.items.next(this.items.value);
    this.saveCart();
  }

  updateLocalCart(updatedItem) {
    const index = this.items?.value?.findIndex((o) => o.barcode.id === updatedItem.barcode.id);
    this.items.value[index].quantity += updatedItem.quantity;
    this.saveCart();
  }

  itemInCart(item): boolean {
    return this.items?.value?.findIndex((o) => o.barcode.id === item.id) > -1;
  }

  getItems() {
    return this.items;
  }

  saveCart(): void {
    this.storageHelper.setData({ cart_items: JSON.stringify(this.items.value) });
  }

  getCart(): any[] {
    return JSON.parse(this.storageHelper.getData('cart_items')) ?? [];
  }
  loadCart(): void {
    this.items.next(JSON.parse(this.storageHelper.getData('cart_items')) ?? []);
  }

  clearCart() {
    this.items.next([]);
    this.storageHelper.remove('cart_items');
  }

  removeItem(item) {
    const index = this.items.value.findIndex((o) => o.product.id === item.product.id);
    if (index > -1) {
      if (item.quantity > 1) {
        this.items.value[index].quantity -= 1;
        this.items.next(this.items.value);
        this.saveCart();
      } else {
        this.items.value.splice(index, 1);
        this.items.next(this.items.value);
        this.saveCart();
      }
    }
  }

  getCurrentUser(): Observable<UserType> {
    return this.currentUserGQL.fetch().pipe(
      map(({ data }: any) => {
        return data?.currentUser;
      }),
    );
  }

  addUserToNewsLetter(pos: string, emailAddress: string): Observable<NewsletterType> {
    return this.addUserToNewsLetterGQL.mutate({ target: { pos }, email: emailAddress }).pipe(
      map(({ data }: any) => {
        return data.addUserToNewsLetter;
      }),
    );
  }

  addToWishList(id: string): Observable<ProductType> {
    return this.markSimpleProductAsFavoriteGQL.mutate({ productId: id }).pipe(
      map(({ data }: any) => {
        // let product;
        let filtredProducts;
        // let productsByUrlKey;
        this.favoriteProducts.next([...(this.favoriteProducts.value || []), data.markSimpleProductAsFavorite]);
        // if (
        //   this.productsGroup?.value?.TOP_PRODUCTS?.length &&
        //   this.productsGroup?.value?.TOP_PRODUCTS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value?.TOP_PRODUCTS?.find((prd) => prd.id === id);
        //   product.isFavorite = true;
        //   this.productsGroup.next(this.productsGroup.value);
        // }
        // if (
        //   this.productsGroup.value?.FEATURED_PRODUCTS?.length &&
        //   this.productsGroup.value?.FEATURED_PRODUCTS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value.FEATURED_PRODUCTS?.find((prd) => prd.id === id);
        //   product.isFavorite = true;
        //   this.productsGroup.next(this.productsGroup.value);
        // }
        // if (
        //   this.productsGroup.value?.NEW_ARRIVALS?.length &&
        //   this.productsGroup.value?.NEW_ARRIVALS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value.NEW_ARRIVALS?.find((prd) => prd.id === id);
        //   product.isFavorite = true;
        //   this.productsGroup.next(this.productsGroup.value);
        // }
        // if (
        //   this.productsGroup.value?.BEST_SELLERS?.length &&
        //   this.productsGroup.value?.BEST_SELLERS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value.BEST_SELLERS?.find((prd) => prd.id === id);
        //   product.isFavorite = true;
        //   this.productsGroup.next(this.productsGroup.value);
        // }
        if (this.recentlyViewed?.value?.length && this.recentlyViewed.value?.find((product) => product.id === id)) {
          // const recentlyViewed = this.recentlyViewed.value?.find((product) => product.id === id);
          // recentlyViewed.isFavorite = true;
          this.recentlyViewed.next(this.recentlyViewed.value);
        }
        if (this.filtredProducts?.value && this.filtredProducts.value?.objects?.find((product) => product.id === id)) {
          filtredProducts = this.filtredProducts.value!.objects?.find((product) => product.id === id);
          filtredProducts.isFavorite = true;
          this.filtredProducts.next(this.filtredProducts.value);
        }
        if (this.relatedProducts?.value?.length && this.relatedProducts?.value?.find((product) => product.id === id)) {
          // const relatedProducts = this.relatedProducts.value?.find((product) => product.id === id);
          // relatedProducts.isFavorite = true;
          this.relatedProducts.next(this.relatedProducts.value);
        }
        if (this.productsByUrlKey?.value?.length && this.productsByUrlKey?.value?.find((product) => product.id === id)) {
          // productsByUrlKey = this.productsByUrlKey.value?.find((product) => product.id === id);
          // productsByUrlKey.isFavorite = true;
          // this.productsByUrlKey.next(this.productsByUrlKey.value);
        }
        if (this.product?.value && this.product?.value?.id === id) {
          this.product.next({ ...this.product.value, isFavorite: true });
        }
        return data.markSimpleProductAsFavorite;
      }),
    );
  }

  removeSimpleProductFromFavorites(id: string): Observable<InternalProductType> {
    return this.removeSimpleProductFromFavoritesGQL.mutate({ productId: id }).pipe(
      map(({ data }: any) => {
        // let product;
        // let filtredProducts;
        const index = this.favoriteProducts.value?.findIndex((item) => item.id === id);
        this.favoriteProducts.value.splice(index, 1);
        this.favoriteProducts.next(this.favoriteProducts.value);
        // if (
        //   this.productsGroup.value?.TOP_PRODUCTS?.length &&
        //   this.productsGroup.value?.TOP_PRODUCTS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value?.TOP_PRODUCTS?.find((prd) => prd.id === id);
        //   product.isFavorite = false;
        //   this.productsGroup.next(this.productsGroup?.value);
        // }
        // if (
        //   this.productsGroup.value?.FEATURED_PRODUCTS?.length &&
        //   this.productsGroup.value?.FEATURED_PRODUCTS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value?.FEATURED_PRODUCTS?.find((prd) => prd.id === id);
        //   product.isFavorite = false;
        //   this.productsGroup.next(this.productsGroup?.value);
        // }
        // if (this.productsGroup.value?.NEW_ARRIVALS.length && this.productsGroup.value?.NEW_ARRIVALS?.find((product) => product.id === id)) {
        //   product = this.productsGroup.value?.NEW_ARRIVALS?.find((prd) => prd.id === id);
        //   product.isFavorite = false;
        //   this.productsGroup.next(this.productsGroup.value);
        // }
        // if (
        //   this.productsGroup.value?.BEST_SELLERS?.length &&
        //   this.productsGroup.value?.BEST_SELLERS?.find((product) => product.id === id)
        // ) {
        //   product = this.productsGroup.value?.BEST_SELLERS?.find((prd) => prd.id === id);
        //   product.isFavorite = false;
        //   this.productsGroup.next(this.productsGroup.value);
        // }
        // if (this.recentlyViewed.value.length && this.recentlyViewed?.value?.find((product) => product.id === id)) {
        //   // const recentlyViewed = this.recentlyViewed.value?.find((product) => product.id === id);
        //   // recentlyViewed.isFavorite = false;
        //   this.recentlyViewed.next(this.recentlyViewed.value);
        // }
        // if (this.filtredProducts.value && this.filtredProducts.value?.objects?.find((product) => product.id === id)) {
        //   filtredProducts = this.filtredProducts.value?.objects?.find((product) => product.id === id);
        //   filtredProducts.isFavorite = false;
        //   this.filtredProducts.next(this.filtredProducts.value);
        // }
        // if (this.relatedProducts?.value?.length && this.relatedProducts?.value?.find((product) => product.id === id)) {
        //   // const relatedProducts = this.relatedProducts.value?.find((product) => product.id === id);
        //   // relatedProducts.isFavorite = true;
        //   this.relatedProducts.next(this.relatedProducts.value);
        // }
        // if (this.product?.value && this.product?.value?.id === id) {
        //   this.product.next({ ...this.product.value, isFavorite: false });
        // }
        return data.removeSimpleProductFromFavorites;
      }),
    );
  }

  getFavoriteSimpleProductByCurrentUser(): Observable<InternalProductPaginateType> {
    return this.getFavoriteSimpleProductByCurrentUserGQL.fetch().pipe(
      map(({ data }: any) => {
        this.favoriteProducts.next(data.getFavoriteSimpleProductByCurrentUser.objects);
        return data.getFavoriteSimpleProductByCurrentUser;
      }),
    );
  }

  createRecentlyViewed(productId: string): Observable<SuccessResponseDtoType> {
    return this.createRecentlyViewedGQL.mutate({ product: productId }).pipe(
      map(({ data }: any) => {
        return data.createRecentlyViewed;
      }),
    );
  }

  getRecentlyViewedProductsByUserPaginated(): Observable<InternalProductPaginateType> {
    return this.getRecentlyViewedProductsByUserPaginatedGQL.fetch().pipe(
      map(({ data }: any) => {
        this.recentlyViewed.next(data.getRecentlyViewedProductsByUserPaginated.objects);
        return data.getRecentlyViewedProductsByUserPaginated.objects;
      }),
    );
  }

  getJobDefinitionsByTargetAndStatusPagination(pos: string, status: JobDefinitionStatusEnum): Observable<JobDefinitionPaginatedType> {
    return this.getJobDefinitionsByTargetAndStatusPaginationGQL
      .fetch({ target: { pos }, pagination: { page: this.productsPageIndex, limit: this.jobLimit }, status })
      .pipe(
        map(({ data }: any) => {
          this.jobs.next(data.getJobDefinitionsByTargetAndStatusPagination);
          return data.getJobDefinitionsByTargetAndStatusPagination;
        }),
      );
  }

  jobDefinition(id: string): Observable<JobDefinitionType> {
    return this.jobDefinitionGQL.fetch({ id }).pipe(
      map(({ data }: any) => {
        this.job.next(data.jobDefinition);
        return data.jobDefinition;
      }),
    );
  }

  createJobApplication(input: JobApplicationInput): Observable<JobApplicationType> {
    return this.createJobApplicationGQL.mutate({ input }).pipe(
      map(({ data }: any) => {
        return data.createJobApplication;
      }),
    );
  }

  createDocument(input: DocumentInput): Observable<DocumentType> {
    return this.createDocumentGQL.mutate({ input }).pipe(
      map(({ data }: any) => {
        return data.createDocument;
      }),
    );
  }

  findContentTypeByType(type: string): Observable<ContentTypeType> {
    return this.findContentTypeByTypeGQL.fetch({ type }).pipe(
      map(({ data }: any) => {
        return data.findContentTypeByType;
      }),
    );
  }

  createRequest(pos: string, input: RequestInput): Observable<RequestType> {
    return this.createRequestGQL
      .mutate({
        input: {
          ...input,
          target: { pos },
        },
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.createRequest;
          }
        }),
      );
  }

  createEventInvite(pos: string, input: EventInviteInput): Observable<any> {
    return this.createEventInviteGQL
      .mutate({
        input: {
          ...input,
          target: { pos },
        },
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.createEventInvite;
          }
          return null;
        }),
        catchError((err) => {
          console.error('🔥 Event Invite creation failed:', err);
          return of(null);
        }),
      );
  }

  getEventInvites(pos: string, userId: string): Observable<any[]> {
    const variables = {
      pos,
      userId: [userId],
    };

    return this.getEventInvitesWithFilterGQL.fetch(variables).pipe(
      map((res: any) => res.data?.getEventInvitesWithFilter || []),
      catchError((err) => {
        console.error('🔥 Failed to fetch event invites:', err);
        return of([]);
      }),
    );
  }

  createEventTicket(pos: string, input: EventTicketInput): Observable<any> {
    return this.insertEventTickets
      .mutate({
        input: {
          ...input,
          target: { pos },
        },
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.insertEventTickets;
          }
          return null;
        }),
        catchError((err) => {
          console.error('🔥 Ticket creation failed:', err);
          return of(null);
        }),
      );
  }

  markTicketAsAttended(ticketId: string): Observable<any> {
    return this.markTicketAsAttendedGQL
      .mutate({
        id: ticketId,
      })
      .pipe(
        map(({ data }: any) => data?.markTicketAsAttended || null),
        catchError((err) => {
          console.error('🔥 Mark ticket as attended failed:', err);
          return of(null);
        }),
      );
  }

  getEventTickets(pos: string, userId: string): Observable<any[]> {
    const variables = {
      pos,
      userId: [userId],
    };

    return this.getEventTicketsWithFilter.fetch(variables).pipe(
      map((res: any) => res.data?.getEventTicketsWithFilter || []),
      catchError((err) => {
        console.error('🔥 Failed to fetch event tickets:', err);
        return of([]);
      }),
    );
  }

  getPartnershipNetworks(pos: string, pagination?: { page: number; limit: number }): Observable<any[]> {
    return this.getPartnershipNetworksByTargetPaginationGQL
      .fetch({
        pos,
        pagination: pagination || { page: 0, limit: 100 }, // Default to get all items
      })
      .pipe(
        map((res: any) => {
          return res.data?.getPartnershipNetworksByTargetPagination?.objects || [];
        }),
        catchError((err) => {
          console.error('🔥 Failed to fetch partnerships:', err);
          return of([]);
        }),
      );
  }

  getCorporateUsersByTargetAndWholesaler(pos: string, wholesalerId: string): Observable<any[]> {
    return this.getCorporateUsersByTargetAndWholesalerGQL.fetch({ pos, wholesalerId }).pipe(
      map((res: any) => res.data?.getCorporateUsersByTargetAndWholesaler || []),
      catchError((err) => {
        console.error('🔥 Failed to fetch corporate users:', err);
        return of([]);
      }),
    );
  }

  getProgramEvents(eventId: string, page: number = 0, limit: number = 150): Observable<any[]> {
    const variables = {
      event: eventId,
      pagination: { page, limit },
    };

    return this.getProgramsByEventPaginationGQL.fetch(variables).pipe(
      map((res: any) => {
        return res.data?.getProgramsByEventPagination?.objects || [];
      }),
      catchError((err) => {
        console.error('🔥 Failed to fetch program events:', err);
        return of([]);
      }),
    );
  }

  getEventTicketByReference(reference: string, pos: string): Observable<any> {
    const variables = {
      reference,
      pos,
    };

    return this.getEventTicketByReferenceGQL.fetch(variables).pipe(
      map((res: any) => res.data?.getEventTicketByReference || null),
      catchError((err) => {
        console.error('🔥 Failed to fetch event ticket by reference:', err);
        return of(null);
      }),
    );
  }

  getSideBlogs(pos: string, page: number, limit: number): Observable<BlogType[]> {
    return this.findBlogsByTargetPaginatedGQL
      .fetch({
        target: { pos },
        pagination: { page, limit },
        status: [VisibilityStatusEnum.PUBLISHED],
      })
      .pipe(
        map(({ data }: any) => {
          return data.findBlogsByTargetPaginated.objects;
        }),
      );
  }

  findBlogsByTargetAndUrl(pos: string, url: string): Observable<BlogType> {
    return this.findBlogsByTargetAndUrlGQL.fetch({ target: { pos }, url }).pipe(
      map(({ data }: any) => {
        this.blog.next(data.findBlogsByTargetAndUrl);
        return data.findBlogsByTargetAndUrl;
      }),
    );
  }

  searchBlogsByTargetPaginated(pos: string): Observable<BlogType[]> {
    return this.findBlogsByTargetPaginatedGQL
      .fetch({
        target: { pos },
        pagination: { page: this.productsPageIndex, limit: this.blogLimit },
        searchString: this.searchString,
        status: VisibilityStatusEnum.PUBLISHED,
      })
      .pipe(
        map(({ data }: any) => {
          this.blogs.next(data.findBlogsByTargetPaginated);
          return data.findBlogsByTargetPaginated.objects;
        }),
      );
  }

  findBlogsByTargetPaginated(pos: string, limit?: number): Observable<BlogType[]> {
    return this.findBlogsByTargetPaginatedGQL
      .fetch({
        target: { pos },
        pagination: { page: this.productsPageIndex, limit: limit ? limit : this.blogLimit },
        searchString: this.searchString,
        status: VisibilityStatusEnum.PUBLISHED,
      })
      .pipe(
        map(({ data }: any) => {
          this.blogs.next(data.findBlogsByTargetPaginated);
          return data.findBlogsByTargetPaginated.objects;
        }),
      );
  }

  getBlogsByTargetPaginated(pos: string, page: number, limit: number): Observable<BlogType[]> {
    return this.findBlogsByTargetPaginatedGQL
      .fetch({
        target: { pos },
        pagination: { page, limit },
        searchString: this.searchString,
        status: VisibilityStatusEnum.PUBLISHED,
      })
      .pipe(
        map(({ data }: any) => {
          this.blogs.next(data.findBlogsByTargetPaginated);
          return data.findBlogsByTargetPaginated.objects;
        }),
      );
  }
}
